// Dossier d'un mail : le mail et ses pièces jointes, traités en une seule tâche. Une seule
// décision (entité, indication, classement, règlement) s'applique à l'ensemble, chaque pièce
// restant corrigible à part. On ne demande rien seulement quand une règle couvre le cas.

import { z } from 'zod'
import { formaterJour } from './dates'
import { REGLEMENTS_PIECE, type StatutPiece, type StatutTache } from './domaine'
import { formaterMontant } from './montant'
import {
  type EffetPiece,
  type EntiteRef,
  type ExtractionPiece,
  fournisseurConnu,
  type TiersRef,
} from './pieces'

/** Action de la tâche ouverte pour trancher un mail et ses pièces. */
export const ACTION_DOSSIER_MAIL = 'traiter_mail'

/**
 * Décisions qu'une pièce venue d'un mail n'ouvre plus à part : elles se prennent dans la
 * tâche du mail (sous-décisions, cachées des listes).
 */
export const DECISIONS_DU_DOSSIER = ['verifier_piece', 'choisir_rubrique', 'decider_reglement']

/**
 * Ce qui couvre le rattachement : une consigne de tri, le fournisseur connu, une échéance,
 * la réponse d'un tiers qu'une tâche attendait.
 */
export type RegleCouvrante =
  | 'consigne'
  | 'consigne_fournisseur'
  | 'bien'
  | 'fournisseur'
  | 'echeance'
  | 'reponse'
  | 'operation'

/** Le motif ne nomme aucune entité : une associée qui voit la pièce le lit. */
export const MOTIF_SANS_REGLE =
  'Aucune règle ne couvre ce mail (consigne de tri, fournisseur connu ou échéance) : entité et suites à confirmer.'

/**
 * Une règle couvre-t-elle le rattachement d'une pièce venue d'un mail à cette entité ?
 * Consigne de tri du mail sur la même entité ; consigne du fournisseur qui y a mené la pièce ;
 * bien du référentiel de cette entité dont la pièce lit l'adresse ; fournisseur (ou client)
 * déjà connu dont l'entité par défaut est celle-là, s'il n'a pas de consignes (un seul
 * endroit) ; règle d'échéance (avis qui fixe un montant, accusé qui prouve, avis d'une
 * échéance déjà faite, paiement attendu d'une échéance) ; réponse d'un tiers qu'une tâche de
 * cette entité attendait ; opération qui la paie, seule et sûre, chez cette entité
 * (`entiteParLesOperations`).
 * Une entité lue seulement sur la pièce (SIREN, nom du destinataire) ne suffit
 * pas : un humain confirme, une fois par fournisseur.
 */
export function regleCouvrante(e: {
  extraction: ExtractionPiece
  entites: readonly EntiteRef[]
  tiers: readonly TiersRef[]
  entiteId: string
  effets: readonly EffetPiece[]
  consigneEntiteId: string | null
  /** Entité de la tâche dont le mail est la réponse attendue d'un tiers. */
  reponseEntiteId: string | null
  /** Paiements attendus portés par une échéance. */
  paiementsDEcheance: readonly string[]
  /** Entité où la consigne du fournisseur a mené la pièce, s'il y en a une. */
  consigneFournisseurEntiteId?: string | null
  /** Le fournisseur a des consignes (plusieurs endroits) : son entité par défaut ne vaut rien. */
  fournisseurAConsignes?: boolean
  /** Entité que l'opération qui paie la facture a désignée (pièce lue sans entité). */
  operationEntiteId?: string | null
  /** Entité du bien du référentiel dont l'adresse, lue sur la pièce, a tranché. */
  bienEntiteId?: string | null
}): RegleCouvrante | null {
  if (e.consigneEntiteId !== null && e.consigneEntiteId === e.entiteId) return 'consigne'
  if (e.consigneFournisseurEntiteId && e.consigneFournisseurEntiteId === e.entiteId)
    return 'consigne_fournisseur'
  if (e.bienEntiteId && e.bienEntiteId === e.entiteId) return 'bien'
  if (e.reponseEntiteId !== null && e.reponseEntiteId === e.entiteId) return 'reponse'
  if (e.operationEntiteId && e.operationEntiteId === e.entiteId) return 'operation'
  const echeance = e.effets.some(
    (x) =>
      x.type === 'fixer_montant' ||
      x.type === 'preuve' ||
      x.type === 'joindre_echeance' ||
      ((x.type === 'lier_paiement' || x.type === 'acquitter_paiement') &&
        e.paiementsDEcheance.includes(x.paiementId)),
  )
  if (echeance) return 'echeance'
  const connu = fournisseurConnu(e.extraction, e.entites, e.tiers)
  if (!e.fournisseurAConsignes && connu?.entiteParDefautId === e.entiteId) return 'fournisseur'
  return null
}

export type EtatDossierMail = 'en_lecture' | 'a_trancher' | 'tranche'

/**
 * À trancher dès qu'une décision est ouverte ; en lecture tant qu'une pièce est lue ou
 * attend son placement ; tranché sinon (rattachées, écartées, doublons).
 */
export function etatDossierMail(
  pieces: readonly { statut: StatutPiece; aPlacer: boolean }[],
  decisionsOuvertes: number,
): EtatDossierMail {
  if (decisionsOuvertes > 0) return 'a_trancher'
  if (pieces.some((p) => p.statut === 'recue' || p.statut === 'extraite' || p.aPlacer))
    return 'en_lecture'
  return 'tranche'
}

const OUVERTES: readonly StatutTache[] = ['a_faire', 'en_cours', 'en_attente_tiers']

/**
 * Que faire de la tâche du mail : l'ouvrir (décision « Trancher le mail »), la rouvrir, la
 * clore quand tout est tranché, en créer une déjà faite à constater quand une règle a tout
 * couvert ; « à part » : la tâche du mail est une autre tâche close (demande faite, tâche
 * d'échéance), les décisions restent visibles à part plutôt que de la rouvrir. Une ancienne
 * « Vérifier un mail » (d'avant le dossier) sur un mail qui ne porte que des pièces se ferme
 * comme « Trancher le mail » : il n'y reste rien à décider.
 */
export function issueDossierMail(
  etat: EtatDossierMail,
  tache: { action: string | null; statut: StatutTache; echeanceId: string | null } | null,
  classement?: string | null,
): 'ouvrir' | 'rouvrir' | 'clore' | 'constater' | 'a_part' | null {
  const ouverte = tache !== null && OUVERTES.includes(tache.statut)
  const verificationDePieces =
    tache?.action === 'verifier_mail' && classement === 'facture_ou_piece'
  const duDossier =
    (tache?.action === ACTION_DOSSIER_MAIL || verificationDePieces) && tache?.echeanceId === null
  if (etat === 'a_trancher') {
    if (!tache) return 'ouvrir'
    if (ouverte) return null
    return duDossier ? 'rouvrir' : 'a_part'
  }
  if (etat === 'tranche') {
    if (!tache) return 'constater'
    return ouverte && duDossier ? 'clore' : null
  }
  return null
}

const couper = (t: string, n = 120) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t)

export function titreDossierMail(objet: string | null, expediteur: string | null): string {
  if (objet) return couper(`Trancher le mail : ${objet}`)
  return couper(expediteur ? `Trancher le mail de ${expediteur}` : 'Trancher un mail reçu')
}

export function titreConstatMail(objet: string | null, expediteur: string | null): string {
  if (objet) return couper(`Mail traité : ${objet}`)
  return couper(expediteur ? `Mail traité de ${expediteur}` : 'Mail traité')
}

export const LIBELLES_REGLE: Record<RegleCouvrante, string> = {
  consigne: 'consigne de tri',
  consigne_fournisseur: 'consigne du fournisseur',
  bien: 'bien du référentiel',
  fournisseur: 'fournisseur connu',
  echeance: 'règle d’échéance',
  reponse: 'réponse attendue',
  operation: 'opération qui la paie',
}

/** Ce qu'un effet de rattachement créera ou fera, en clair (proposition ou constat). */
export function decrireEffet(e: EffetPiece, tiers: string | null): string {
  switch (e.type) {
    case 'creer_paiement': {
      const montant = formaterMontant(e.montantDuCts, e.devise)
      if (e.dejaRegle) return `Paiement déjà réglé : ${montant}, à rapprocher du relevé`
      const sens = e.sens === 'payer' ? 'à payer' : 'à encaisser'
      const qui = tiers ? ` ${e.sens === 'payer' ? 'à' : 'de'} ${tiers}` : ''
      const quand = e.datePrevue ? ` le ${formaterJour(e.datePrevue)}` : ''
      const tache =
        e.sens === 'payer' && (e.moyen === 'virement_emis' || e.moyen === 'cheque')
          ? ', et une tâche « Payer »'
          : ''
      return `Paiement attendu : ${montant} ${sens}${qui}${quand}${tache}`
    }
    case 'lier_paiement':
      return 'Justifie le paiement attendu correspondant'
    case 'acquitter_paiement':
      return 'Acquitte le paiement attendu correspondant (payé, à rapprocher)'
    case 'fixer_montant':
      return `Fixe le montant de l’échéance : ${formaterMontant(e.montantCts)}`
    case 'preuve':
      return 'Preuve de l’échéance'
    case 'joindre_echeance':
      return 'Avis de l’échéance déjà faite, au même montant'
    case 'creer_tache':
      return `Tâche « ${e.titre} »${e.dateDue ? ` pour le ${formaterJour(e.dateDue)}` : ''}`
  }
}

// Décision unique du dossier

const indicationSaisie = z.string().trim().min(1).max(500)

/**
 * La décision prise dans la tâche du mail : entité, indication (écrite ou dictée), dossier de
 * classement et règlement valent pour toutes ses pièces ; chaque pièce peut être corrigée à
 * part (autre entité, autre indication, écartée). « Suites » : créer ce que la lecture
 * propose (paiement attendu, tâche « Payer », tâche d'un courrier).
 */
export const decisionDossierMail = z.object({
  entiteId: z.uuid().optional(),
  indication: indicationSaisie.optional(),
  /** Retenir l'entité pour ce fournisseur : ses prochaines pièces s'y rattachent seules. */
  retenirFournisseur: z.boolean().optional(),
  /** Dossier du plan de classement, pour les pièces de son entité ; sinon Claude place. */
  dossierId: z.uuid().optional(),
  /** Facture reçue dans une boîte personnelle : réglée par la société, ou avancée. */
  reglement: z.enum(REGLEMENTS_PIECE).optional(),
  avanceePar: z.uuid().optional(),
  suites: z.boolean().optional(),
  pieces: z
    .array(
      z.object({
        pieceId: z.uuid(),
        ecarter: z.boolean().optional(),
        entiteId: z.uuid().optional(),
        indication: indicationSaisie.optional(),
        dossierId: z.uuid().optional(),
        suites: z.boolean().optional(),
      }),
    )
    .max(50)
    .default([])
    .refine((l) => new Set(l.map((p) => p.pieceId)).size === l.length, {
      error: 'Une pièce citée deux fois.',
    }),
})
export type DecisionDossierMail = z.infer<typeof decisionDossierMail>

/** Ce qui s'applique à une pièce : sa correction, sinon la décision d'ensemble. */
export function choixPiece(
  d: DecisionDossierMail,
  pieceId: string,
): {
  ecarter: boolean
  entiteId: string | null
  indication: string | null
  dossierId: string | null
  suites: boolean
} {
  const p = d.pieces.find((x) => x.pieceId === pieceId)
  return {
    ecarter: p?.ecarter ?? false,
    entiteId: p?.entiteId ?? d.entiteId ?? null,
    indication: p?.indication ?? d.indication ?? null,
    dossierId: p?.dossierId ?? d.dossierId ?? null,
    suites: p?.suites ?? d.suites ?? true,
  }
}
